import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";

import type { ThemePreference } from "@tsmono/theme/bootstrap";

import { SETTINGS_STORAGE_KEY } from "../theme/constants";

export type { ThemePreference };
export { SETTINGS_STORAGE_KEY };

const MAX_SEARCH_MODEL_HISTORY = 25;

// Mirrors scout's userSettings: a single zustand-persisted store for
// localStorage-backed UI preferences, ready for more settings to land here
// rather than each growing its own bespoke record. The persisted key/shape is
// shared with the inline theme bootstrap, which reads it via
// `readThemePreference` (@tsmono/theme) with this same SETTINGS_STORAGE_KEY.
interface UserSettingsState {
  themePreference: ThemePreference;
  setThemePreference: (themePreference: ThemePreference) => void;
  showRetriedLogs: boolean;
  setShowRetriedLogs: (showRetriedLogs: boolean) => void;
  searchModelHistory: string[];
  recordSearchModel: (model: string) => void;
  clearSearchModelHistory: () => void;
}

export const useUserSettings = create<UserSettingsState>()(
  persist(
    (set) => ({
      // Default to the readable (Event Colors) variant per product decision.
      themePreference: "readable-system",
      setThemePreference: (themePreference: ThemePreference) => {
        set({ themePreference });
      },
      showRetriedLogs: false,
      setShowRetriedLogs: (showRetriedLogs: boolean) => {
        set({ showRetriedLogs });
      },
      searchModelHistory: [],
      recordSearchModel: (model: string) => {
        const trimmedModel = model.trim();
        if (!trimmedModel) {
          return;
        }

        const normalizedModel = trimmedModel.toLowerCase();
        set((state) => ({
          searchModelHistory: [
            trimmedModel,
            ...state.searchModelHistory.filter(
              (existingModel) =>
                existingModel.trim().toLowerCase() !== normalizedModel
            ),
          ].slice(0, MAX_SEARCH_MODEL_HISTORY),
        }));
      },
      clearSearchModelHistory: () => {
        set({ searchModelHistory: [] });
      },
    }),
    {
      name: SETTINGS_STORAGE_KEY,
      storage: createJSONStorage(() => localStorage),
    }
  )
);
